import { useCallback, useMemo } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import type { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { usePerformanceQuery } from "app/pages/digital-marketing/ad-intelligence/common/overview/hooks/usePerformanceQuery";
import type { PerformanceResponse } from "app/pages/digital-marketing/ad-intelligence/common/overview/types";
import { isRequestedDateBeforeMay2025 } from "app/pages/digital-marketing/ad-intelligence/common/overview/utils/utils";
import { allTrackers } from "app/services/track/track";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { entityTypeMap } from "pro-features/components/AutocompleteAdIntelligence/AutocompleteAdIntelligenceHelper";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { publisherAnalysisNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisNavigator";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../../../common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { UpdatedMetricBadge } from "../../../../common/overview/PerformanceOverviewWidget";
import { defaultMinHeight, getEmptyState } from "../../../../common/overview/PerformanceWidget";
import { AdIntelligenceHookButton } from "../../../../common/shared/TableComponents";
import type { AdIntelligenceController } from "../../../../common/shared/types/types";
import {
    hasAdIntelligenceOverviewOpenAccess,
    isAdIntelligenceNoTouchPreviewVariant,
    openAdIntelligenceHookModal,
} from "../../../../common/utils/utils";

import { getPriceRangeLabel } from "./constants";
import { PerformanceLoadingState } from "./loading-pixel";
import { SpendAndImpressionsContainer, WidgetContentContainer } from "./styled";

const WidgetTitleBadgeWrapper = styled(Flex)`
    margin-left: 8px;
`;

interface ISpendAndImpressionsWidgetProps {
    controller: AdIntelligenceController;
    combinedSubtitleFilters: ReturnType<typeof useSubtitleFilters>;
    showPerformanceEmptyState: boolean;
}

const SpendAndImpressionsContent = ({
    controller,
    showPerformanceEmptyState,
}: Pick<ISpendAndImpressionsWidgetProps, "controller" | "showPerformanceEmptyState">) => {
    const { translate } = useTranslate();
    const hasAdIntelBeta = hasAdIntelligenceOverviewOpenAccess(true);
    const allParams = useDisplayAdvertiserPublisherDataAndParams();
    const { duration, ...params } = allParams;
    const isBeforeMay2025 = isRequestedDateBeforeMay2025(duration);
    const { data, isLoading: isPerformanceLoading, isError } = usePerformanceQuery(controller);
    const isNoTouchVariant = isAdIntelligenceNoTouchPreviewVariant(true);
    const priceRangeLabel = useMemo(() => {
        if (isPerformanceLoading || isError) {
            return "N/A";
        }

        if (!data?.records) {
            return "N/A";
        }

        const recordItems = Object.values(data.records);
        if (recordItems.length === 0) {
            return "N/A";
        }

        const firstItem = recordItems[0] as PerformanceResponse;

        if (!firstItem.spend) {
            return "N/A";
        }

        return getPriceRangeLabel(firstItem.spend?.value ?? 0);
    }, [data, isPerformanceLoading, isError]);

    const handleSeeAll = useCallback(() => {
        if (hasAdIntelBeta) {
            allTrackers.dispatchTrackEvent("Internal Link", "click", "spend & impressions/see metrics");
            if (controller === entityTypeMap.advertiser) {
                void displayAdvertisingNavigation.overview.go({
                    ...params,
                    webSource: devicesTypes.TOTAL,
                    duration,
                });
            } else {
                void publisherAnalysisNavigation.overview.go({
                    ...params,
                    webSource: devicesTypes.DESKTOP,
                    duration,
                });
            }
        } else {
            openAdIntelligenceHookModal(controller);
        }
    }, [hasAdIntelBeta, controller, params, duration]);

    if (isPerformanceLoading) {
        return (
            <WidgetContentContainer>
                <PerformanceLoadingState />
            </WidgetContentContainer>
        );
    }

    if (showPerformanceEmptyState) {
        return <WidgetContentContainer>{getEmptyState(isError, true, defaultMinHeight)}</WidgetContentContainer>;
    }

    if (isBeforeMay2025) {
        return (
            <Stack
                height="100%"
                width="100%"
                justifyContent="center"
                alignItems="center"
            >
                <Text
                    fontVariant="subtitle1"
                    color={colorsPalettes.carbon[400]}
                >
                    {translate("ad.intelligence.overview.spend.and.impressions.data.available.may.2025")}
                </Text>
                <Text
                    fontVariant="caption"
                    color={colorsPalettes.carbon[400]}
                >
                    {translate("ad.intelligence.overview.spend.and.impressions.try.another.period")}
                </Text>
            </Stack>
        );
    }

    if (!hasAdIntelBeta) {
        return (
            <SpendAndImpressionsContainer>
                <Text
                    fontVariant="button"
                    color={colorsPalettes.carbon[400]}
                >
                    {translate(`ad.intelligence.${controller}.overview.spend.and.impressions.preview.subtitle`)}
                </Text>

                <AdIntelligenceHookButton
                    dataAutomation="ad-intelligence-spend-impressions-hook-button"
                    buttonText={translate("ad.intelligence.overview.preview.no.access.button")}
                    isNoTouchVariant={isNoTouchVariant}
                    onButtonClick={handleSeeAll}
                />
            </SpendAndImpressionsContainer>
        );
    }

    return (
        <SpendAndImpressionsContainer
            $blurContent={false}
            gap="0"
        >
            <Text
                padding="24px"
                alignSelf="start"
                fontVariant="h3"
                color={colorsPalettes.carbon[500]}
            >
                {priceRangeLabel}
            </Text>
            <Box
                as="hr"
                width="100%"
                margin="0"
                borderColor={colorsPalettes.carbon[50]}
            />
            <Flex
                alignItems="center"
                justifyContent="end"
                width="100%"
                padding="8px 16px"
            >
                <Button
                    onClick={handleSeeAll}
                    type="flat"
                >
                    {translate("ad.intelligence.overview.spend.and.impressions.preview.button")}
                </Button>
            </Flex>
        </SpendAndImpressionsContainer>
    );
};

export const SpendAndImpressionsWidget = ({
    controller,
    combinedSubtitleFilters,
    showPerformanceEmptyState,
}: ISpendAndImpressionsWidgetProps) => {
    const { translate } = useTranslate();
    return (
        <WidgetContainer
            title={translate(`ad.intelligence.${controller}.overview.spend.and.impressions.widget.title`)}
            headerTitleSuffix={
                <WidgetTitleBadgeWrapper
                    inline
                    alignItems="center"
                >
                    <UpdatedMetricBadge />
                </WidgetTitleBadgeWrapper>
            }
            tooltip={translate(`ad.intelligence.${controller}.overview.spend.and.impressions.widget.title.tooltip`)}
            subtitleFilters={combinedSubtitleFilters}
        >
            <SpendAndImpressionsContent
                showPerformanceEmptyState={showPerformanceEmptyState}
                controller={controller}
            />
        </WidgetContainer>
    );
};
